// src/pages/Profile.tsx
import {
  IonActionSheet,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonChip,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonModal,
  IonPage,
  IonRow,
  IonSpinner,
  IonText,
  IonTitle,
  useIonAlert,
  useIonToast
} from '@ionic/react';
import {
  analyticsOutline,
  bookmark,
  briefcaseOutline,
  checkmarkCircle,
  chevronDownOutline,
  chevronUpOutline,
  cloudDownloadOutline,
  cloudUploadOutline,
  codeSlashOutline,
  documentAttachOutline,
  documentTextOutline,
  ellipsisVertical,
  eyeOutline,
  libraryOutline,
  listOutline,
  logOutOutline,
  pencilOutline,
  personCircleOutline,
  refreshOutline,
  searchOutline,
  settingsOutline,
  shareOutline,
  shieldCheckmarkOutline,
  star,
  starOutline,
  trashOutline
} from 'ionicons/icons';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import CreativeLoader from '../components/CreativeLoader.tsx';
import { Resume } from '../components/ResumeCard.tsx';
import ShareModal from '../components/ShareModal.tsx';
import TitleBar from '../components/TitleBar.tsx';
import PreferenceOnboardingModal from '../components/PreferenceOnboardingModal.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { supabase } from '../lib/supabaseClient.ts';
import { auth, user as userApi, billing, jobs } from '../services/api.ts';
import PreferenceService from '../services/PreferenceService.ts';
import { usePreferenceSSE } from '../services/sse/hooks/usePreferenceSSE.ts';
import { UserPreferences } from '../types/preferences.ts';
import {
  getContactInfo,
  getFormattedExperience,
  getTechnicalSkills,
  ResumeAnalysisData as UtilsResumeAnalysisData
} from '../utils/resumeAnalysisUtils.ts';
import { getDefaultResume, hasActiveResumeStatuses } from '../utils/resumeUtils.ts';
import { mapCeleryStatus } from '../utils/taskStatus.ts';

// --- Data Interfaces ---
interface BasicInfo { name: string; title: string; location: string; summary: string; }
interface ProfessionalIdentity { primary_role: string; seniority_level: string; years_of_experience: number; industry_focus: string[]; }
interface HardSkill { name: string; }
interface Experience { company: string; title: string; start_date: string; end_date: string; description: string; location: string; hard_skills_used: string[]; }
interface Education { institution: string; degree: string; field: string; start_date: string; end_date: string; }

// Enhanced responsive utilities for professional mobile experience
const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setScreenSize({
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        width,
        height
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

// Mobile-first design tokens
const mobileTokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32
  },
  borderRadius: {
    sm: 6,
    md: 12,
    lg: 16
  },
  typography: {
    h1: { mobile: 20, desktop: 24 },
    h2: { mobile: 18, desktop: 20 },
    h3: { mobile: 16, desktop: 18 },
    body: { mobile: 14, desktop: 16 },
    caption: { mobile: 12, desktop: 14 }
  },
  touchTarget: {
    min: 44, // iOS HIG minimum
    comfortable: 48
  }
};

// Professional Loading Animation Component
const ExperienceSetupAnimation: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const setupSteps = [
    { icon: shieldCheckmarkOutline, text: "Securing your profile data", color: '#4A6FA5' },
    { icon: analyticsOutline, text: "Analyzing your career journey", color: '#2D4A6B' },
    { icon: libraryOutline, text: "Building your professional portfolio", color: '#5A7C99' },
    { icon: settingsOutline, text: "Optimizing your experience", color: '#3B5171' }
  ];

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % setupSteps.length);
    }, 2500);

    return () => {
      clearInterval(stepInterval);
    };
  }, []);

  const currentStepData = setupSteps[currentStep];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: '120px',
      textAlign: 'center',
      background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 50%, #f1f3f4 100%)',
      minHeight: '80vh',
      margin: '-16px',
      color: '#2c3e50'
    }}>
      {/* Professional Logo/Icon Area */}
      <div style={{
        position: 'relative',
        marginBottom: '48px',
        padding: '24px',
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef'
      }}>
        <IonIcon
          icon={currentStepData.icon}
          style={{
            fontSize: '4rem',
            color: currentStepData.color,
            animation: 'professionalFade 2.5s ease-in-out infinite'
          }}
        />
      </div>

      {/* Professional Title */}
      <h2 style={{
        fontSize: '1.75rem',
        fontWeight: '600',
        margin: '0 0 16px 0',
        color: '#2c3e50',
        letterSpacing: '0.5px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        Preparing Your Dashboard
      </h2>

      {/* Current Step */}
      <p style={{
        fontSize: '1.1rem',
        fontWeight: '400',
        margin: '8px 0 40px',
        color: '#495057',
        maxWidth: '360px',
        lineHeight: '1.4'
      }}>
        {currentStepData.text}
      </p>

      {/* Professional Progress Bar */}
      <div style={{
        width: '280px',
        height: '4px',
        background: '#e9ecef',
        borderRadius: '2px',
        marginBottom: '32px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${((currentStep + 1) / setupSteps.length) * 100}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #4A6FA5 0%, #2D4A6B 100%)',
          borderRadius: '2px',
          transition: 'width 0.8s ease-out',
          animation: 'progressShimmer 2s infinite'
        }} />
      </div>

      {/* Step Indicators */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '40px'
      }}>
        {setupSteps.map((step, index) => (
          <div
            key={index}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '4px',
              background: index <= currentStep ? step.color : '#dee2e6',
              transition: 'all 0.6s ease'
            }}
          />
        ))}
      </div>

      {/* Professional Loading Indicator */}
      <div style={{
        display: 'flex',
        gap: '6px',
        alignItems: 'center'
      }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#6c757d',
              animation: `professionalPulse 1.4s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes professionalFade {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes progressShimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        @keyframes professionalPulse {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// --- Reusable UI Components (exactly like ResumeAnalysis) ---
const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <IonCard style={{ marginTop: '20px', borderRadius: '12px', '--background': 'var(--app-card-background-color)', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}>
    <div style={{ padding: '16px', borderBottom: '1px solid var(--app-border-color)', display: 'flex', alignItems: 'center' }}>
      <IonIcon icon={icon} color="primary" style={{ fontSize: '20px', marginRight: '12px' }} />
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--app-text-color)', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ padding: '16px', color: 'var(--app-text-color-secondary)', lineHeight: 1.6 }}>
      {children}
    </div>
  </IonCard>
);

// ExperienceCard component (copied exactly from ResumeAnalysis)
interface ExperienceItem {
  title: string;
  company: string;
  start_date: string;
  end_date: string;
  description: string;
  key_achievements?: string[];
}

const ExperienceCard: React.FC<{ item: ExperienceItem }> = ({ item }) => (
  <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--app-border-color)', borderBottomStyle: 'dashed' }} className="last:border-b-0 last:pb-0 last:mb-0">
    <h4 style={{ fontWeight: '600', color: 'var(--app-text-color)', margin: '0 0 4px' }}>{item.title}</h4>
    <p style={{ color: 'var(--app-text-color)', fontWeight: '500', margin: 0 }}>{item.company}</p>
    <p style={{ fontSize: '0.875rem', color: 'var(--app-text-color-secondary)', margin: '4px 0 0' }}>{item.start_date} - {item.end_date}</p>
    <p style={{ fontSize: '1rem', margin: '12px 0' }}>{item.description}</p>
    {item.key_achievements && item.key_achievements.length > 0 && (
      <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
        {item.key_achievements.map((achievement, index) => (
          <li key={index} style={{ marginBottom: '4px', fontSize: '0.95rem' }}>{achievement}</li>
        ))}
      </ul>
    )}
  </div>
);

// Adapter functions to convert Profile data structure to ResumeAnalysis format

const Profile: React.FC = () => {
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const history = useHistory();
  const location = useLocation();
  const { session, loading: authLoading, signOut } = useAuth();
  const { isMobile, isTablet, isDesktop, width } = useResponsive();

  // Add professional mobile animations
  useEffect(() => {
    const styles = document.createElement('style');
    styles.textContent = `
      .mobile-action-sheet {
        --backdrop-opacity: 0.4;
      }
      .mobile-action-sheet .action-sheet-wrapper {
        border-radius: ${mobileTokens.borderRadius.lg}px ${mobileTokens.borderRadius.lg}px 0 0;
        padding: ${mobileTokens.spacing.lg}px;
      }
      .mobile-fade-in {
        animation: mobileSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      @keyframes mobileSlideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .mobile-card-hover {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .mobile-card-hover:active {
        transform: scale(0.98);
        opacity: 0.8;
      }
      .resume-item-hover {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .resume-item-hover:hover {
        --background: linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(168, 85, 247, 0.02) 100%) !important;
        transform: translateX(4px);
      }
      ion-chip {
        cursor: default;
      }
      ion-chip:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(99, 102, 241, 0.2);
      }

      /* Enhanced Action Sheet Styling */
      .action-sheet-group {
        margin-bottom: 0 !important;
        box-shadow: none !important;
      }

      .action-sheet-group:last-child {
        margin-top: 0 !important;
      }

      ion-action-sheet {
        --backdrop-opacity: 0.5;
      }

      ion-action-sheet .action-sheet-wrapper {
        border-radius: ${mobileTokens.borderRadius.lg}px ${mobileTokens.borderRadius.lg}px 0 0 !important;
        padding: 0 !important;
      }

      ion-action-sheet .action-sheet-group {
        border-radius: 0 !important;
        margin: 0 !important;
      }

      ion-action-sheet .action-sheet-container {
        padding: 0 !important;
      }

      ion-action-sheet .action-sheet-title {
        font-weight: 600;
        font-size: 18px;
        color: #1f2937;
        padding: 20px 16px 8px;
      }

      ion-action-sheet .action-sheet-sub-title {
        font-size: 14px;
        color: #6b7280;
        padding: 0 16px 16px;
      }

      /* Button styling with icons */
      ion-action-sheet button.action-sheet-button {
        height: 56px;
        font-size: 16px;
        font-weight: 500;
        transition: all 0.2s ease;
      }

      ion-action-sheet button.action-sheet-button ion-icon {
        font-size: 22px;
        margin-right: 16px;
        transition: transform 0.2s ease;
      }

      ion-action-sheet button.action-sheet-button:hover ion-icon {
        transform: scale(1.1);
      }

      /* Primary action styling */
      .action-sheet-primary {
        color: #667eea !important;
        font-weight: 600 !important;
      }

      .action-sheet-primary ion-icon {
        color: #667eea !important;
      }

      .action-sheet-primary:hover {
        background: rgba(102, 126, 234, 0.08) !important;
      }

      /* Warning action (Retry) */
      .action-sheet-warning {
        color: #f59e0b !important;
      }

      .action-sheet-warning ion-icon {
        color: #f59e0b !important;
      }

      .action-sheet-warning:hover {
        background: rgba(245, 158, 11, 0.08) !important;
      }

      /* Destructive action (Delete) */
      .action-sheet-destructive {
        color: #ef4444 !important;
        font-weight: 600 !important;
      }

      .action-sheet-destructive ion-icon {
        color: #ef4444 !important;
      }

      .action-sheet-destructive:hover {
        background: rgba(239, 68, 68, 0.08) !important;
      }

      /* Cancel button */
      .action-sheet-cancel {
        font-weight: 500 !important;
        color: #6b7280 !important;
        border-top: 1px solid #e5e7eb !important;
        padding-top: 12px !important;
        margin-top: 4px !important;
      }

      .action-sheet-cancel:hover {
        background: rgba(107, 114, 128, 0.05) !important;
      }
    `;
    document.head.appendChild(styles);
    return () => {
      if (document.head.contains(styles)) {
        document.head.removeChild(styles);
      }
    };
  }, [isMobile]);

  // Cache keys must be user-specific to prevent data leakage between accounts
  const getCacheKey = (key: string) => {
    const userId = session?.user?.id;
    return userId ? `${key}_${userId}` : null;
  };

  // Helper to mark data as loaded (persists across navigation)
  const markDataAsLoaded = () => {
    const userId = session?.user?.id;
    if (userId) {
      sessionStorage.setItem(`profile_loaded_${userId}`, 'true');
      setDataLoaded(true);
    }
  };

  // Helper to clear loaded flag (on logout or user change)
  const clearDataLoadedFlag = () => {
    const userId = session?.user?.id;
    if (userId) {
      sessionStorage.removeItem(`profile_loaded_${userId}`);
      setDataLoaded(false);
    }
  };

  const [allResumes, setAllResumes] = useState<Resume[]>(() => {
    // Initialize with cached resumes for instant display
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return [];
    const cachedResumes = localStorage.getItem(`profile_resumes_${cachedUserId}`);
    if (!cachedResumes) return [];
    try {
      return JSON.parse(cachedResumes);
    } catch {
      return [];
    }
  });
  const [latestAnalysis, setLatestAnalysis] = useState<UtilsResumeAnalysisData | null>(() => {
    // Initialize with cached analysis for instant display
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return null;
    const cachedAnalysis = localStorage.getItem(`profile_analysis_${cachedUserId}`);
    if (!cachedAnalysis) return null;
    try {
      return JSON.parse(cachedAnalysis);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(() => {
    // Initialize as true if we have cached data
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return false;
    const wasLoaded = sessionStorage.getItem(`profile_loaded_${cachedUserId}`) === 'true';
    return wasLoaded;
  });
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hasActiveStatuses, setHasActiveStatuses] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  // Preference onboarding state
  const [showPreferenceOnboarding, setShowPreferenceOnboarding] = useState(false);
  const [showPreferenceUpdatePrompt, setShowPreferenceUpdatePrompt] = useState(false);
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [hasCompletedOnboardingBefore, setHasCompletedOnboardingBefore] = useState(() => {
    return localStorage.getItem('preference_onboarding_completed') === 'true';
  });
  // Show setup animation only on the first visit per browser session
  const [isSettingUpExperience, setIsSettingUpExperience] = useState<boolean>(() => !sessionStorage.getItem('profileSetupDone'));
  const [isMounting, setIsMounting] = useState(() => {
    // Only show mounting state if we don't have cached data
    // Get cached user ID directly from localStorage (set during previous sessions)
    const cachedUserId = localStorage.getItem('cached_user_id');

    if (!cachedUserId) return true; // No cached user, show loader

    // Check if data was loaded this session
    const wasLoaded = sessionStorage.getItem(`profile_loaded_${cachedUserId}`) === 'true';

    // Also check if we have cached resumes
    const hasCachedResumes = localStorage.getItem(`profile_resumes_${cachedUserId}`) !== null;

    // Only show loader if no cached data exists
    return !wasLoaded && !hasCachedResumes;
  });
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [uploadFileName, setUploadFileName] = useState('');
  const [actionSheetOpen, setActionSheetOpen] = useState<string | null>(null);
  const [actionSheetButtons, setActionSheetButtons] = useState<any[]>([]);
  const [showAllResumes, setShowAllResumes] = useState(false);
  const showMoreButtonRef = useRef<HTMLDivElement>(null);

  // Saved Jobs state
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [savedJobsLoading, setSavedJobsLoading] = useState(false);

  // Credit data state (matching Usage.tsx exactly)
  interface CreditBalance {
    user_id: string;
    balance: number;
    balance_display?: string;
    last_updated?: string;
    updated_on?: string;
  }

  interface CreditTransaction {
    id: string;
    user_id: string;
    transaction_type: 'purchase' | 'usage' | 'bonus' | 'refund';
    amount: number;
    balance_after: number;
    description: string;
    stripe_session_id?: string;
    ai_action_type?: string;
    price_display?: string;
    created_at?: string;
    created_on?: string;
  }

  interface UsageSummary {
    total_calls: number;
    total_credits_used: number;
    avg_credits_per_call: number;
    credits_by_feature: Record<string, number>;
  }

  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(() => {
    // Initialize with cached credit balance for instant display
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return null;
    const cached = localStorage.getItem(`credit_balance_${cachedUserId}`);
    if (!cached) return null;
    try {
      return JSON.parse(cached);
    } catch {
      return null;
    }
  });
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>(() => {
    // Initialize with cached transactions for instant display
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return [];
    const cached = localStorage.getItem(`credit_transactions_${cachedUserId}`);
    if (!cached) return [];
    try {
      return JSON.parse(cached);
    } catch {
      return [];
    }
  });
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(() => {
    // Initialize with cached summary for instant display
    const cachedUserId = localStorage.getItem('cached_user_id');
    if (!cachedUserId) return null;
    const cached = localStorage.getItem(`usage_summary_${cachedUserId}`);
    if (!cached) return null;
    try {
      return JSON.parse(cached);
    } catch {
      return null;
    }
  });
  const creditRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Resume completion popup state
  const previousProcessingResumesRef = useRef<Set<string>>(new Set());
  // Generate action sheet buttons for a resume
  const generateActionSheetButtons = (resume: Resume, displayName: string) => {
    const buttons = [];

    // Check Status button for processing/queued resumes
    if (resume.analysis_status === 'processing' || resume.analysis_status === 'queued') {
      buttons.push({
        text: 'Check Status',
        icon: refreshOutline,
        cssClass: 'action-sheet-primary',
        handler: () => {
          presentToast({
            message: 'Checking resume status...',
            duration: 2000,
            color: 'primary'
          });
          loadResumeData(false, true); // Force reload
        }
      });
    }

    // Retry button for failed resumes
    if (resume.analysis_status === 'failed') {
      buttons.push({
        text: 'Retry Analysis',
        icon: refreshOutline,
        cssClass: 'action-sheet-warning',
        handler: () => handleRetry(resume.id)
      });
    }

    // Standard buttons
    buttons.push(
      {
        text: 'Rename',
        icon: pencilOutline,
        handler: () => handleRename(resume.id, displayName)
      },
      {
        text: 'Download',
        icon: cloudDownloadOutline,
        handler: () => handleDownload(resume.id)
      },
      {
        text: 'Download Templated PDF',
        icon: documentTextOutline,
        handler: () => handleDownloadTemplated(resume.id)
      },
      {
        text: 'Delete',
        icon: trashOutline,
        role: 'destructive',
        cssClass: 'action-sheet-destructive',
        handler: () => handleDelete(resume.id, displayName)
      },
      {
        text: 'Cancel',
        role: 'cancel',
        cssClass: 'action-sheet-cancel'
      }
    );

    return buttons;
  };

  // Silent version for auto-setting (no toast messages)
  const setDefaultSilently = async (resumeId: string) => {
    try {
      // Call backend API to set default resume
      await userApi.setDefaultResume(resumeId);

      // Update local state to mark this resume as default
      const updatedResumes = allResumes.map(resume => ({
        ...resume,
        is_default: resume.id === resumeId
      }));
      setAllResumes(updatedResumes);

      // Update localStorage immediately with user-specific key
      const resumesCacheKey = getCacheKey('profile_resumes');
      if (resumesCacheKey) {
        localStorage.setItem(resumesCacheKey, JSON.stringify(updatedResumes));
      }

      // Load the analysis for the new default resume
      const targetResume = allResumes.find(r => r.id === resumeId);
      if (targetResume && targetResume.analysis_status === 'completed') {
        const analysis = await userApi.getResumeAnalysis(resumeId);

        // Handle different possible response structures
        let analysisData = null;
        if (analysis.analysis) {
          analysisData = analysis.analysis;
        } else if (analysis.data) {
          analysisData = analysis.data;
        } else if (analysis.basic_info) {
          // Direct analysis object
          analysisData = analysis;
        }

        setLatestAnalysis(analysisData);

        // Cache analysis to localStorage with user-specific key
        const analysisCacheKey = getCacheKey('profile_analysis');
        if (analysisCacheKey) {
          localStorage.setItem(analysisCacheKey, JSON.stringify(analysisData));
        }
      }
    } catch (error) {
      console.error('Failed to set default resume silently:', error);
    }
  };

  // Set as default handler with toast messages for user actions
  const handleSetDefault = async (resumeId: string) => {
    try {
      // Update local state to mark this resume as default (immediate visual feedback)
      const updatedResumes = allResumes.map(resume => ({
        ...resume,
        is_default: resume.id === resumeId
      }));
      setAllResumes(updatedResumes);

      // Update localStorage immediately with user-specific key
      const resumesCacheKey = getCacheKey('profile_resumes');
      if (resumesCacheKey) {
        localStorage.setItem(resumesCacheKey, JSON.stringify(updatedResumes));
      }

      // Show brief success feedback first
      presentToast({
        message: 'Setting as main resume...',
        duration: 1500,
        color: 'primary'
      });

      // Call backend API to set default resume
      await userApi.setDefaultResume(resumeId);

      // Small delay for visual feedback (user sees star change)
      await new Promise(resolve => setTimeout(resolve, 800));

      // Now start loading the analysis
      setLoading(true);

      // Clear current analysis to prevent stale data
      setLatestAnalysis(null);

      // Load the analysis for the new default resume
      const targetResume = allResumes.find(r => r.id === resumeId);
      if (targetResume && targetResume.analysis_status === 'completed') {
        const analysis = await userApi.getResumeAnalysis(resumeId);

        // Handle different possible response structures
        let analysisData = null;
        if (analysis.analysis) {
          analysisData = analysis.analysis;
        } else if (analysis.data) {
          analysisData = analysis.data;
        } else if (analysis.basic_info) {
          // Direct analysis object
          analysisData = analysis;
        }

        setLatestAnalysis(analysisData);

        // Cache analysis to localStorage with user-specific key
        const analysisCacheKey = getCacheKey('profile_analysis');
        if (analysisCacheKey) {
          localStorage.setItem(analysisCacheKey, JSON.stringify(analysisData));
        }
      }

      // Data has been loaded and will be shown in the main view

      // Final success message
      presentToast({
        message: 'Main resume updated successfully!',
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to set main resume:', error);
      presentToast({
        message: 'Failed to set main resume.',
        duration: 3000,
        color: 'danger'
      });
    } finally {
      // Remove loading state
      setLoading(false);
    }
  };

  // Preference SSE integration - listen for preference extraction events
  usePreferenceSSE(async (event) => {
    console.log('🔥 [PREFERENCE SSE] Event received:', event);
    console.log('🔥 [PREFERENCE SSE] Event type:', event.type);
    console.log('🔥 [PREFERENCE SSE] Should show onboarding:', event.should_show_onboarding);

    // When preferences are extracted, load them and show onboarding if needed
    if (event.type === 'preferences_extracted') {
      console.log('🔥 [PREFERENCE SSE] preferences_extracted event detected!');
      console.log('🔥 [PREFERENCE SSE] Event should_show_onboarding:', event.should_show_onboarding);

      try {
        console.log('🔥 [PREFERENCE SSE] Fetching preferences from API...');
        const preferences = await PreferenceService.getPreferences();
        console.log('🔥 [PREFERENCE SSE] Preferences loaded:', preferences);
        console.log('🔥 [PREFERENCE SSE] low_confidence_sections:', preferences.low_confidence_sections);
        console.log('🔥 [PREFERENCE SSE] should_show_onboarding:', preferences.should_show_onboarding);

        setUserPreferences(preferences);

        // Show onboarding if either the event or the preferences say so
        if (event.should_show_onboarding || preferences.should_show_onboarding) {
          console.log('🔥 [PREFERENCE SSE] ✅ Preferences extracted');

          // Check if user has completed onboarding before
          const hasCompletedBefore = localStorage.getItem('preference_onboarding_completed') === 'true';

          if (hasCompletedBefore) {
            // Show simple update prompt for subsequent resumes
            console.log('🔥 [PREFERENCE SSE] Showing simple update prompt (not first time)');
            setShowPreferenceUpdatePrompt(true);
          } else {
            // Show full onboarding for first resume
            console.log('🔥 [PREFERENCE SSE] Showing full onboarding (first time)');
            setShowPreferenceOnboarding(true);
          }
        } else {
          console.log('⚠️ [PREFERENCE SSE] NOT showing onboarding - conditions not met');
        }

        presentToast({
          message: '✨ We analyzed your resume and extracted your preferences!',
          duration: 3000,
          color: 'success',
        });
      } catch (error) {
        console.error('❌ [PREFERENCE SSE] Failed to load preferences:', error);
      }
    } else if (event.type === 'preferences_updated') {
      console.log('🔥 [PREFERENCE SSE] preferences_updated event detected');
      // Preferences were updated elsewhere, refresh them
      try {
        const preferences = await PreferenceService.getPreferences();
        setUserPreferences(preferences);
      } catch (error) {
        console.error('❌ [PREFERENCE SSE] Failed to load preferences:', error);
      }
    }
  });

  // Rename handler
  const handleRename = (resumeId: string, currentName: string) => {
    setRenamingId(resumeId);
    setNewName(currentName);
  };

  const saveRename = async () => {
    if (!renamingId || !newName.trim()) return;

    try {
      // Update local state immediately
      const updatedResumes = allResumes.map(resume =>
        resume.id === renamingId
          ? { ...resume, custom_name: newName.trim() }
          : resume
      );
      setAllResumes(updatedResumes);

      // Update localStorage immediately with user-specific key
      const resumesCacheKey = getCacheKey('profile_resumes');
      if (resumesCacheKey) {
        localStorage.setItem(resumesCacheKey, JSON.stringify(updatedResumes));
      }

      // Make API call to save the custom name
      await userApi.updateResumeName(renamingId, newName.trim());

      presentToast({
        message: 'Resume renamed successfully!',
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to rename resume:', error);
      presentToast({
        message: 'Failed to rename resume.',
        duration: 3000,
        color: 'danger'
      });
    } finally {
      setRenamingId(null);
      setNewName('');
    }
  };

  const cancelRename = () => {
    setRenamingId(null);
    setNewName('');
  };

  // Perform the actual upload
  const performUpload = async (file: File, customName: string) => {
    try {
      setIsUploading(true);

      // Show upload starting notification
      presentToast({
        message: `Uploading "${customName}"...`,
        duration: 2000,
        color: 'primary'
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('custom_name', customName);

      const uploadResult = await userApi.uploadResume(formData);

      console.log('✅ Upload result:', uploadResult);

      presentToast({
        message: 'Resume uploaded successfully!',
        duration: 2000,
        color: 'success'
      });

      // Refresh the resume list to get the newly uploaded resume
      await loadResumeData(false, true);

      console.log('✅ Resume uploaded - analysis will start shortly');

      presentToast({
        message: 'Resume uploaded! Analysis will start shortly.',
        duration: 3000,
        color: 'success'
      });
    } catch (error: any) {
      console.error('❌ Upload failed:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });

      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Failed to upload resume.';

      presentToast({
        message: errorMessage,
        duration: 4000,
        color: 'danger'
      });
    } finally {
      setIsUploading(false);
      setUploadFileName('');
    }
  };

  // Handle file upload with custom name
  const handleResumeUpload = async (file: File) => {
    if (!file) return;

    // Set default filename from the file
    const defaultName = file.name.replace(/\.[^/.]+$/, '');
    setUploadFileName(defaultName);

    // Show filename editing prompt
    presentAlert({
      header: 'Upload Resume',
      message: 'Enter a name for your resume:',
      inputs: [
        {
          name: 'filename',
          type: 'text',
          value: defaultName,
          placeholder: 'Resume name'
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Upload',
          handler: (data) => {
            if (!data.filename?.trim()) {
              presentToast({
                message: 'Please enter a valid name',
                duration: 2000,
                color: 'warning'
              });
              return false;
            }

            // Perform upload with the custom name (non-async)
            performUpload(file, data.filename.trim());
            return true;
          }
        }
      ]
    });
  };

  // Logout handler
  const handleLogout = async () => {
    presentAlert({
      header: 'Logout',
      message: 'Are you sure you want to logout?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Logout',
          handler: async () => {
            try {
              console.log('🔓 Logging out...');

              // CRITICAL: Sign out from Supabase FIRST to clear session
              await signOut();
              console.log('🔓 Supabase signOut() called');

              // Wait for session to actually be cleared (check up to 2 seconds)
              let attempts = 0;
              while (attempts < 20) {
                const { data } = await supabase.auth.getSession();
                if (!data.session) {
                  console.log('🔓 Supabase session confirmed cleared');
                  break;
                }
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
              }

              // CRITICAL: Clear Supabase session storage
              // Be specific - only clear session tokens, not OAuth flow state
              const localKeysToRemove: string[] = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                // Only clear actual session tokens, not OAuth PKCE state
                if (key && (
                  key.startsWith('sb-') && key.includes('-auth-token')
                )) {
                  localKeysToRemove.push(key);
                }
              }
              localKeysToRemove.forEach(key => {
                console.log(`🔓 Removing localStorage key: ${key}`);
                localStorage.removeItem(key);
              });

              // Clear sessionStorage - only our app data, not Supabase OAuth state
              const sessionKeysToRemove: string[] = [];
              for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                // Only clear our app's session data (profile_loaded, profileSetupDone, jobs_data_loaded)
                // Do NOT clear Supabase OAuth state
                if (key && (key.startsWith('profile_') || key.includes('profileSetupDone') || key.includes('jobs_data_loaded'))) {
                  sessionKeysToRemove.push(key);
                }
              }
              sessionKeysToRemove.forEach(key => {
                console.log(`🔓 Removing sessionStorage key: ${key}`);
                sessionStorage.removeItem(key);
              });

              // Clear backend tokens (if any)
              await auth.logout();
              console.log('🔓 Backend tokens cleared');

              // Clear cached profile data on logout (user-specific keys)
              const resumesCacheKey = getCacheKey('profile_resumes');
              const analysisCacheKey = getCacheKey('profile_analysis');
              if (resumesCacheKey) localStorage.removeItem(resumesCacheKey);
              if (analysisCacheKey) localStorage.removeItem(analysisCacheKey);

              // Also clear any old non-user-specific cache keys (for migration)
              localStorage.removeItem('profile_resumes');
              localStorage.removeItem('profile_analysis');

              // Clear the data loaded flag
              clearDataLoadedFlag();

              // Clear cached user ID
              localStorage.removeItem('cached_user_id');

              // Final verification: Ensure session is truly cleared
              const { data: finalCheck } = await supabase.auth.getSession();
              if (finalCheck.session) {
                console.warn('🔓 Session still exists after cleanup! This should not happen.');
                // Force signOut one more time
                await supabase.auth.signOut({ scope: 'local' });
              }

              console.log('🔓 All caches and sessions cleared, redirecting to login');

              // Give AuthContext time to update (it listens to auth state changes)
              await new Promise(resolve => setTimeout(resolve, 200));

              // Use React Router navigation (not hard reload)
              // This preserves the app but AuthContext should show logged out state
              history.replace('/login');
            } catch (error) {
              console.error('🔓 Logout error:', error);
              presentToast({
                message: 'Logout failed. Please try again.',
                duration: 3000,
                color: 'danger'
              });
            }
          },
        },
      ],
    });
  };

  // Single unified function to load resume data
  // src/pages/Profile.tsx

  const loadResumeData = useCallback(async (isInitialLoad = false, forceReload = false) => {
    const logPrefix = `🔄 [loadResumeData${isInitialLoad ? ' - Initial' : ''}]`;

    // Don't make API calls if user is not authenticated
    if (!session && !auth.isAuthenticated()) {
      console.log(`${logPrefix} User not authenticated - skipping API call`);
      return;
    }

    // Check sessionStorage directly to see if data was already loaded this session
    const userId = session?.user?.id;
    const wasLoadedThisSession = userId && sessionStorage.getItem(`profile_loaded_${userId}`) === 'true';

    // Only load data if it's not already loaded, unless forced
    if ((dataLoaded || wasLoadedThisSession) && !forceReload) {
      console.log(`${logPrefix} Data already loaded - skipping API call`);
      return;
    }

    // Show loading when fetching data
    setLoading(true);

    try {
      const { resumes } = await userApi.listResumes();
      if (resumes && resumes.length > 0) {
        console.log('📄 Raw resumes from API:', resumes.map((r: any) => ({
          id: r.id,
          name: r.custom_name || r.file_name,
          raw_status: r.analysis_status
        })));

        let mappedResumes: Resume[] = resumes.map((r: any) => ({
          ...r,
          upload_timestamp: r.uploaded_at,
          analysis_status: mapCeleryStatus(r.analysis_status),
          // Handle missing is_default field - if undefined, set to false initially
          is_default: r.is_default ?? false
        }));
        mappedResumes.sort((a: Resume, b: Resume) => new Date(b.upload_timestamp).getTime() - new Date(a.upload_timestamp).getTime());

        console.log('🌟 Mapped resumes:', mappedResumes.map(r => ({
          id: r.id,
          name: r.custom_name || r.file_name,
          is_default: r.is_default,
          mapped_status: r.analysis_status
        })));

        // If no resume is marked as default, mark the first completed one as default
        const hasDefaultResume = mappedResumes.some(r => r.is_default);
        if (!hasDefaultResume) {
          // Find the first completed resume or just the first one
          const defaultCandidate = mappedResumes.find(r => r.analysis_status === 'completed') || mappedResumes[0];
          if (defaultCandidate) {
            console.log('🌟 Auto-setting resume as main:', defaultCandidate.id);
            defaultCandidate.is_default = true;
            // Set as default silently (no toast messages)
            await setDefaultSilently(defaultCandidate.id);
          }
        }

        // Always update the full list of resumes
        setAllResumes(mappedResumes);
        setHasActiveStatuses(hasActiveResumeStatuses(mappedResumes));

        // Cache resumes to localStorage with user-specific key
        const resumesCacheKey = getCacheKey('profile_resumes');
        if (resumesCacheKey) {
          localStorage.setItem(resumesCacheKey, JSON.stringify(mappedResumes));
        }

        // Use the new default resume logic
        const defaultResume = getDefaultResume(mappedResumes);

        if (defaultResume) {
          // Always try to load analysis for the default resume if we don't have it
          if (!latestAnalysis) {
            try {
              const analysis = await userApi.getResumeAnalysis(defaultResume.id);

              // Handle different possible response structures
              let analysisData = null;
              if (analysis.analysis) {
                analysisData = analysis.analysis;
              } else if (analysis.data) {
                analysisData = analysis.data;
              } else if (analysis.basic_info) {
                // Direct analysis object
                analysisData = analysis;
              }

              setLatestAnalysis(analysisData);

              // Cache analysis to localStorage with user-specific key
              const analysisCacheKey = getCacheKey('profile_analysis');
              if (analysisCacheKey) {
                localStorage.setItem(analysisCacheKey, JSON.stringify(analysisData));
              }
            } catch (error) {
              console.error('Failed to load analysis:', error);
              setLatestAnalysis(null);
              // Clear analysis cache on error (user-specific key)
              const analysisCacheKey = getCacheKey('profile_analysis');
              if (analysisCacheKey) {
                localStorage.removeItem(analysisCacheKey);
              }
            }
          }
        } else {
          // If no resume is complete, ensure we don't show an old analysis
          console.log('⚠️ No default resume found - setting analysis to null');
          setLatestAnalysis(null);
        }
        // ------------------------------------

      } else {
        setAllResumes([]);
        setLatestAnalysis(null);
        setHasActiveStatuses(false);

        // Clear localStorage when no resumes (user-specific keys)
        const resumesCacheKey = getCacheKey('profile_resumes');
        const analysisCacheKey = getCacheKey('profile_analysis');
        if (resumesCacheKey) localStorage.removeItem(resumesCacheKey);
        if (analysisCacheKey) localStorage.removeItem(analysisCacheKey);
      }
    } catch (err) {
      console.error(`${logPrefix} error:`, err);
      presentToast({ message: 'Failed to load resume data.', duration: 3000, color: 'danger' });
    } finally {
      // Always clear loading state when done and mark data as loaded
      setLoading(false);
      markDataAsLoaded();
    }
  }, [session, presentToast, history, markDataAsLoaded]);

  // Load cached data immediately when session is available (runs on every mount)
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) return;

    // Check if data was already loaded this session
    const wasLoaded = sessionStorage.getItem(`profile_loaded_${userId}`) === 'true';

    if (wasLoaded) {
      // Data was loaded before, restore from cache
      console.log('💾 Restoring cached data from previous load...');
      const resumesCacheKey = `profile_resumes_${userId}`;
      const analysisCacheKey = `profile_analysis_${userId}`;

      const cachedResumes = localStorage.getItem(resumesCacheKey);
      if (cachedResumes) {
        try {
          const parsedResumes = JSON.parse(cachedResumes);
          console.log(`💾 Restored ${parsedResumes.length} resumes from cache`);
          setAllResumes(parsedResumes);
          setHasActiveStatuses(hasActiveResumeStatuses(parsedResumes));
        } catch (e) {
          console.error('Failed to parse cached resumes:', e);
        }
      }

      const cachedAnalysis = localStorage.getItem(analysisCacheKey);
      if (cachedAnalysis) {
        try {
          setLatestAnalysis(JSON.parse(cachedAnalysis));
          console.log('💾 Restored analysis from cache');
        } catch (e) {
          console.error('Failed to parse cached analysis:', e);
        }
      }

      setDataLoaded(true);
    }
  }, [session?.user?.id]);

  // Load cached data when session becomes available (runs once per session)
  useEffect(() => {
    if (session?.user?.id && !dataLoaded) {
      console.log('💾 Checking for cached profile data...');
      const resumesCacheKey = getCacheKey('profile_resumes');
      const analysisCacheKey = getCacheKey('profile_analysis');

      let hasCachedData = false;

      if (resumesCacheKey) {
        const cachedResumes = localStorage.getItem(resumesCacheKey);
        if (cachedResumes) {
          try {
            const parsedResumes = JSON.parse(cachedResumes);
            console.log(`💾 Found ${parsedResumes.length} cached resumes`);
            setAllResumes(parsedResumes);
            setHasActiveStatuses(hasActiveResumeStatuses(parsedResumes));
            hasCachedData = true;
          } catch (e) {
            console.error('Failed to parse cached resumes:', e);
          }
        }
      }

      if (analysisCacheKey) {
        const cachedAnalysis = localStorage.getItem(analysisCacheKey);
        if (cachedAnalysis) {
          try {
            setLatestAnalysis(JSON.parse(cachedAnalysis));
            console.log('💾 Found cached analysis');
          } catch (e) {
            console.error('Failed to parse cached analysis:', e);
          }
        }
      }

      // Mark as loaded if we found cached data
      if (hasCachedData) {
        console.log('💾 Using cached data - no API call needed');
        markDataAsLoaded();
      } else {
        console.log('💾 No cached data found - will fetch from API');
      }
    }
  }, [session?.user?.id, dataLoaded, getCacheKey, markDataAsLoaded]);

  // Clear old user's cache when user ID changes (authentication change)
  useEffect(() => {
    const currentUserId = session?.user?.id;
    const cachedUserId = localStorage.getItem('cached_user_id');

    if (currentUserId && cachedUserId && currentUserId !== cachedUserId) {
      console.log('🔄 User changed, clearing old user cache');
      // Clear old user's cache
      const oldResumeKey = `profile_resumes_${cachedUserId}`;
      const oldAnalysisKey = `profile_analysis_${cachedUserId}`;
      localStorage.removeItem(oldResumeKey);
      localStorage.removeItem(oldAnalysisKey);

      // Clear non-user-specific keys
      localStorage.removeItem('profile_resumes');
      localStorage.removeItem('profile_analysis');

      // Reset state
      setAllResumes([]);
      setLatestAnalysis(null);
      clearDataLoadedFlag();
    }

    // Update cached user ID
    if (currentUserId) {
      localStorage.setItem('cached_user_id', currentUserId);
    }
  }, [session?.user?.id, clearDataLoadedFlag]);

  // Initial experience setup sequence - load data DURING animation on first visit only
  useEffect(() => {
    let setupTimer: NodeJS.Timeout | null = null;

    // Only start loading if auth is complete and user is authenticated
    if (!authLoading && (session || auth.isAuthenticated())) {
      if (isSettingUpExperience) {
        // First visit: show animation and do an initial data load
        console.log('🎬 First visit: starting animation and loading data...');
        loadResumeData(true);
        // Show experience setup animation for 3s (faster) then mark done
        setupTimer = setTimeout(() => {
          console.log('🎬 Experience setup animation complete');
          sessionStorage.setItem('profileSetupDone', '1');
          setIsSettingUpExperience(false);
          setIsMounting(false);
          setLoading(false);
        }, 3000);
      } else if (!dataLoaded) {
        // Only load if data hasn't been loaded yet
        console.log('🎬 Loading data for the first time...');
        loadResumeData(false);
        // Small delay to ensure data is rendered
        setTimeout(() => setIsMounting(false), 100);
      } else {
        console.log('🎬 Data already loaded - showing cached data');
        // Data is cached, show immediately
        setIsMounting(false);
      }
    } else {
      console.log('🎬 Waiting for authentication before loading data...');
    }

    return () => {
      if (setupTimer) clearTimeout(setupTimer);
    };
  }, [authLoading, session, isSettingUpExperience, dataLoaded]);

  // Smart polling effect - only runs when on Profile dashboard AND there are active statuses
  useEffect(() => {
    const isOnProfileDashboard = location.pathname === '/app/profile';

    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (isOnProfileDashboard && hasActiveStatuses) {
      console.log('▶️ Starting polling - active statuses detected');
      // Start polling every 60 seconds (reduced frequency to reduce server load)
      pollingIntervalRef.current = setInterval(() => {
        loadResumeData(false, true); // Force reload for polling
      }, 60000); // Increased from 30s to 60s

      return () => {
        if (pollingIntervalRef.current) {
          console.log('⏹️ Stopping polling interval');
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      console.log('⏸️ Polling stopped - no active statuses or not on dashboard');
    }
  }, [location.pathname, hasActiveStatuses, loadResumeData]);

  // Track processing resumes for status updates (job search removed - only scraped jobs now)
  useEffect(() => {
    if (!allResumes.length) return;

    // Track which resumes are currently processing
    const currentProcessing = new Set(
      allResumes
        .filter(r => r.analysis_status === 'processing' || r.analysis_status === 'queued')
        .map(r => r.id)
    );

    // Update the ref with current processing resumes
    previousProcessingResumesRef.current = currentProcessing;
  }, [allResumes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (creditRefreshIntervalRef.current) {
        clearInterval(creditRefreshIntervalRef.current);
        creditRefreshIntervalRef.current = null;
      }
    };
  }, []);

  // Load credit data on mount and set up auto-refresh (realtime)
  useEffect(() => {
    // Delay initial load to avoid competing with other initial requests
    const initialLoadTimer = setTimeout(() => {
      loadCreditData();
    }, 1000); // Wait 1 second after mount

    // Set up auto-refresh every 2 minutes (reduced frequency to reduce server load)
    creditRefreshIntervalRef.current = setInterval(() => {
      loadCreditData();
    }, 120000); // 2 minutes - credit data doesn't change frequently

    return () => {
      clearTimeout(initialLoadTimer);
      if (creditRefreshIntervalRef.current) {
        clearInterval(creditRefreshIntervalRef.current);
        creditRefreshIntervalRef.current = null;
      }
    };
  }, []); // Empty deps - loadCreditData is defined later and is stable


  const handleDelete = (resumeId: string, resumeName: string) => {
    presentAlert({
      header: 'Delete Resume',
      message: `Are you sure you want to permanently delete "${resumeName}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            // Execute deletion after dialog dismisses
            setTimeout(async () => {
              // Show deleting toast
              presentToast({
                message: 'Deleting resume...',
                duration: 1500,
                color: 'warning',
                position: 'top'
              });

              setDeletingId(resumeId);
              try {
                await userApi.deleteResume(resumeId);
                await loadResumeData(false, true); // Force reload
                presentToast({
                  message: 'Resume deleted successfully.',
                  duration: 2000,
                  color: 'success',
                  position: 'top'
                });
              } catch (err: any) {
                presentToast({
                  message: err.response?.data?.detail || 'Failed to delete resume.',
                  duration: 3000,
                  color: 'danger',
                  position: 'top'
                });
              } finally {
                setDeletingId(null);
              }
            }, 100); // Small delay to let dialog dismiss
          }
        },
      ],
    });
  };

  const handleRetry = async (resumeId: string) => {
    try {
      await userApi.retryResumeAnalysis(resumeId);
      presentToast({
        message: `Resume analysis retry scheduled successfully!`,
        duration: 3000,
        color: 'success'
      });
      // Refresh resume list to show updated status
      loadResumeData(false, true);
    } catch (err: any) {
      presentToast({
        message: err.response?.data?.detail || 'Failed to retry analysis.',
        duration: 3000,
        color: 'danger'
      });
    }
  };

  const handleShare = (resumeId: string) => {
    setSelectedResumeId(resumeId);
    setShowShareModal(true);
  };


  const handleToggleResumes = () => {
    if (showAllResumes) {
      // Collapsing: scroll to keep the button in view
      if (showMoreButtonRef.current) {
        const buttonPosition = showMoreButtonRef.current.getBoundingClientRect().top;
        const scrollOffset = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = buttonPosition + scrollOffset - 100; // 100px from top

        // First toggle state
        setShowAllResumes(false);

        // Then scroll after a brief delay to let DOM update
        setTimeout(() => {
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }, 50);
      } else {
        setShowAllResumes(false);
      }
    } else {
      // Expanding: just toggle
      setShowAllResumes(true);
    }
  };

  const handleDownload = async (resumeId: string) => {
    try {
      // Prefer original resume download endpoint
      const resMeta = allResumes.find(r => r.id === resumeId);
      const fallback = resMeta?.original_filename || 'resume.pdf';
      const { blob, filename } = await userApi.downloadOriginalResume(resumeId, { fallbackFilename: fallback });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'resume.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download failed', err);
      presentToast({
        message: err?.response?.data?.detail || 'Failed to download resume.',
        duration: 3000,
        color: 'danger'
      });
    }
  };

  const handleDownloadTemplated = async (resumeId: string) => {
    try {
      presentToast({ message: 'Generating templated PDF…', duration: 1500, color: 'primary' });
      const resMeta = allResumes.find(r => r.id === resumeId);
      // If we have the original filename, append _modern.pdf as a sensible default
      const base = resMeta?.original_filename?.replace(/\.[^/.]+$/, '') || 'templated-resume';
      const fallback = `${base}_modern.pdf`;
      const { blob, filename } = await userApi.downloadTemplatedResume(resumeId, { timeout: 120000, fallbackFilename: fallback });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'templated-resume.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Templated download failed', err);
      presentToast({
        message: err?.response?.data?.detail || 'Failed to generate or download templated PDF.',
        duration: 3000,
        color: 'danger'
      });
    }
  };

  // Helper function to format time ago
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Calculate summary from transactions (copied exactly from Usage.tsx)
  const calculateSummary = (txs: CreditTransaction[]): UsageSummary => {
    // Filter for usage transactions only
    const usageTxs = txs.filter(tx => tx.transaction_type === 'usage');

    // Calculate total calls and credits
    const totalCalls = usageTxs.length;
    const totalCreditsUsed = Math.abs(usageTxs.reduce((sum, tx) => sum + tx.amount, 0));

    // Calculate credits by feature
    const creditsByFeature: Record<string, number> = {};
    usageTxs.forEach(tx => {
      const feature = tx.ai_action_type || 'other';
      creditsByFeature[feature] = (creditsByFeature[feature] || 0) + Math.abs(tx.amount);
    });

    return {
      total_calls: totalCalls,
      total_credits_used: totalCreditsUsed,
      avg_credits_per_call: totalCalls > 0 ? totalCreditsUsed / totalCalls : 0,
      credits_by_feature: creditsByFeature,
    };
  };

  // Load credit data (copied exactly from Usage.tsx)
  const loadCreditData = async () => {
    try {
      console.log('🔄 Loading credit data in background...');

      // Add timeout wrapper to prevent hanging (axios already has 30s timeout, but add extra safety)
      const dataPromise = Promise.all([
        billing.getBalance(),
        billing.getTransactions(),
      ]);

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Credit data load timeout')), 25000)
      );

      const [balanceRes, transactionsRes] = await Promise.race([
        dataPromise,
        timeoutPromise,
      ]);

      console.log('✅ Balance response:', balanceRes);
      console.log('✅ Transactions response:', transactionsRes);

      setCreditBalance(balanceRes);
      const txs = transactionsRes.transactions || [];
      setCreditTransactions(txs);

      // Calculate summary from transactions
      const summary = calculateSummary(txs);
      setUsageSummary(summary);

      // Cache credit data for instant loading on next visit
      const userId = session?.user?.id || localStorage.getItem('cached_user_id');
      if (userId) {
        localStorage.setItem(`credit_balance_${userId}`, JSON.stringify(balanceRes));
        localStorage.setItem(`credit_transactions_${userId}`, JSON.stringify(txs));
        localStorage.setItem(`usage_summary_${userId}`, JSON.stringify(summary));
      }

      console.log('✅ Credit data loaded and cached successfully');
    } catch (error: any) {
      console.error('❌ Failed to load credit data:', error);
      // Don't block UI - use cached data if available
      const userId = session?.user?.id || localStorage.getItem('cached_user_id');
      if (userId) {
        try {
          const cachedBalance = localStorage.getItem(`credit_balance_${userId}`);
          const cachedTxs = localStorage.getItem(`credit_transactions_${userId}`);
          const cachedSummary = localStorage.getItem(`usage_summary_${userId}`);

          if (cachedBalance) setCreditBalance(JSON.parse(cachedBalance));
          if (cachedTxs) setCreditTransactions(JSON.parse(cachedTxs));
          if (cachedSummary) setUsageSummary(JSON.parse(cachedSummary));
        } catch (cacheError) {
          console.error('Failed to load cached credit data:', cacheError);
        }
      }
    }
  };

  // Load saved jobs
  const loadSavedJobs = async () => {
    try {
      setSavedJobsLoading(true);
      const response = await jobs.getSavedJobs(1, 5);
      setSavedJobs(response || []);
    } catch (error) {
      console.error('❌ Failed to load saved jobs:', error);
      setSavedJobs([]);
    } finally {
      setSavedJobsLoading(false);
    }
  };

  // Load saved jobs on mount
  useEffect(() => {
    if (session?.user?.id) {
      loadSavedJobs();
    }
  }, [session?.user?.id]);

  const renderContent = () => {
    // Only show loading spinner on first visit animation OR explicit loading states
    if (isSettingUpExperience || isMounting) return null; // full-page loader handles this
    if (loading) return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '200px'
      }}>
        <CreativeLoader
          size="medium"
          message="Loading your profile..."
          showJoke={false}
        />
      </div>
    );

    // Always show resume management section at the top
    const resumeManagementSection = (
      <div style={{
        marginBottom: isMobile ? 0 : mobileTokens.spacing.xl,
        marginTop: isMobile ? 0 : 0
      }}>
        {/* Resume Management Card */}
        {allResumes.length > 0 ? (
          <IonCard className={isMobile ? 'mobile-fade-in' : ''} style={{
            '--background': '#ffffff',
            borderRadius: isMobile ? `${mobileTokens.borderRadius.md}px` : `${mobileTokens.borderRadius.lg}px`,
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            margin: isMobile ? '12px' : 'auto',
            marginBottom: isMobile ? '16px' : 0,
            width: isMobile ? 'calc(100% - 24px)' : 'auto',
            transition: 'all 0.3s ease'
          }}>
            {/* Header inside the card */}
            <IonCardHeader style={{
              borderBottom: '1px solid rgba(99, 102, 241, 0.08)',
              paddingBottom: isMobile ? mobileTokens.spacing.lg : mobileTokens.spacing.lg,
              paddingLeft: isMobile ? mobileTokens.spacing.lg : mobileTokens.spacing.xl,
              paddingRight: isMobile ? mobileTokens.spacing.lg : mobileTokens.spacing.xl,
              paddingTop: isMobile ? mobileTokens.spacing.lg : mobileTokens.spacing.lg,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
            }}>
              {isMobile ? (
                // Mobile: Professional card-like layout
                <IonGrid className="ion-no-padding">
                  <IonRow className="ion-justify-content-between ion-align-items-center">
                    <IonCol>
                      <h2 style={{
                        fontSize: 'clamp(20px, 5vw, 24px)',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        letterSpacing: '-0.02em',
                        padding: 0,
                        margin: 0,
                        lineHeight: 1.2
                      }}>
                        Resumes
                      </h2>
                    </IonCol>
                    <IonCol size="auto">
                      <IonChip outline={false} style={{
                        '--background': '#e3f2fd',
                        '--color': '#1976d2',
                        fontSize: 'clamp(11px, 2.5vw, 13px)',
                        fontWeight: '600',
                        height: 'clamp(22px, 5vw, 26px)',
                        minWidth: 'clamp(22px, 5vw, 26px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {allResumes.length}
                      </IonChip>
                    </IonCol>
                  </IonRow>
                  <IonRow className="ion-justify-content-center" style={{ marginTop: mobileTokens.spacing.md }}>
                    <IonCol size="auto">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={e => e.target.files && handleResumeUpload(e.target.files[0])}
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                      />
                      <IonButton
                        fill="solid"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        style={{
                          '--background': '#1e3a8a',
                          '--color': 'white',
                          '--border-radius': '10px',
                          height: 'clamp(40px, 9vw, 44px)',
                          minWidth: 'clamp(140px, 35vw, 170px)',
                          fontSize: 'clamp(13px, 3.2vw, 14px)',
                          fontWeight: '600',
                          textTransform: 'none',
                          letterSpacing: '0.3px'
                        }}
                      >
                        {isUploading ? (
                          <>
                            <IonSpinner name="crescent" slot="start" style={{ width: '16px', height: '16px' }} />
                            Uploading...
                          </>
                        ) : (
                          <><IonIcon icon={cloudUploadOutline} slot="start" /> Upload</>
                        )}
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : (
                // Desktop: Horizontal layout
                <IonGrid className="ion-no-padding" style={{ width: '100%' }}>
                  <IonRow className="ion-justify-content-between ion-align-items-center" style={{ width: '100%' }}>
                    <IonCol size="4">
                      <IonTitle size="large" style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        letterSpacing: '-0.5px',
                        padding: 0,
                        margin: 0
                      }}>
                        Your Resumes ({allResumes.length})
                      </IonTitle>
                    </IonCol>
                    <IonCol size="4"></IonCol>
                    <IonCol size="4" className="ion-text-right">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={e => e.target.files && handleResumeUpload(e.target.files[0])}
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                      />
                      <IonButton
                        fill="solid"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        style={{
                          '--background': '#1e3a8a',
                          '--color': 'white',
                          height: '32px',
                          minWidth: '120px',
                          fontSize: '14px',
                          fontWeight: '500',
                          textTransform: 'none'
                        }}
                      >
                        {isUploading ? (
                          <>
                            <IonSpinner name="crescent" slot="start" style={{ width: '16px', height: '16px' }} />
                            Uploading...
                          </>
                        ) : (
                          <><IonIcon icon={cloudUploadOutline} slot="start" /> Upload Resume</>
                        )}
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              )}
            </IonCardHeader>

            {/* Resume list */}
            <IonList style={{
              padding: '0',
              overflow: 'hidden',
              borderRadius: isMobile ? `0 0 ${mobileTokens.borderRadius.md}px ${mobileTokens.borderRadius.md}px` : `0 0 ${mobileTokens.borderRadius.lg}px ${mobileTokens.borderRadius.lg}px`
            }}>
              {(showAllResumes ? allResumes : allResumes.slice(0, 2)).map((resume, index) => {
                const filename = resume.original_filename;
                const cleanFilename = filename ? filename.replace(/\.[^/.]+$/, '') : null;
                const displayName = (resume as any).custom_name || cleanFilename || `Resume ${index + 1}`;
                const isDefault = resume.is_default;
                const isRenaming = renamingId === resume.id;
                console.log('🌟 Resume star debug:', {
                  resumeId: resume.id,
                  displayName,
                  is_default: resume.is_default,
                  isDefault,
                  shouldShowBlackStar: isDefault
                });

                // Status display
                let statusIcon, statusText, statusColor;
                if (resume.analysis_status === 'completed') {
                  statusIcon = '✓';
                  statusText = 'Complete';
                  statusColor = '#28a745';
                } else if (resume.analysis_status === 'processing' || resume.analysis_status === 'queued') {
                  statusIcon = '⏰';
                  statusText = 'Processing';
                  statusColor = '#fd7e14';
                } else if (resume.analysis_status === 'retrying') {
                  statusIcon = '🔄';
                  statusText = 'Retrying';
                  statusColor = '#ffc107';
                } else if (resume.analysis_status === 'failed') {
                  statusIcon = '⚠️';
                  statusText = 'Failed';
                  statusColor = '#dc3545';
                } else {
                  // Unknown status - treat as queued
                  statusIcon = '⏰';
                  statusText = 'Queued';
                  statusColor = '#6c757d';
                }

                return (
                  <IonItem
                    key={resume.id}
                    button={resume.analysis_status === 'completed'}
                    onClick={resume.analysis_status === 'completed' ? () => history.push(`/app/profile/analysis/${resume.id}`) : undefined}
                    lines={index < allResumes.length - 1 ? "inset" : "none"}
                    className="resume-item-hover"
                    style={{
                      '--padding-start': isMobile ? '20px' : `${mobileTokens.spacing.xl}px`,
                      '--padding-end': isMobile ? '12px' : `${mobileTokens.spacing.xl}px`,
                      '--padding-top': isMobile ? '16px' : `${mobileTokens.spacing.lg}px`,
                      '--padding-bottom': isMobile ? '16px' : `${mobileTokens.spacing.lg}px`,
                      '--min-height': 'auto',
                      '--background': 'transparent',
                      '--border-color': 'rgba(99, 102, 241, 0.1)',
                      overflow: 'hidden',
                      width: '100%',
                      maxWidth: '100%',
                      cursor: resume.analysis_status === 'completed' ? 'pointer' : 'default',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Resume Content - Mobile Optimized */}
                    {isMobile ? (
                      // Mobile: Clean, minimal tile design
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        overflow: 'hidden'
                      }}>
                        {/* Resume name row - Full width */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          minWidth: 0,
                          width: '100%'
                        }}>
                          <div style={{
                            fontSize: 'clamp(15px, 3.8vw, 17px)',
                            fontWeight: '600',
                            color: '#1a1a1a',
                            lineHeight: 1.3,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {displayName}
                          </div>
                          {isDefault && (
                            <span style={{
                              fontSize: 'clamp(9px, 2.2vw, 10px)',
                              fontWeight: '700',
                              color: '#f57c00',
                              backgroundColor: '#fff3e0',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              flexShrink: 0,
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              height: 'fit-content'
                            }}>
                              MAIN
                            </span>
                          )}
                        </div>

                        {/* Date, status, and action buttons row */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          width: '100%'
                        }}>
                          {/* Left side: Date and status */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexShrink: 1,
                            minWidth: 0
                          }}>
                            <span style={{
                              fontSize: 'clamp(11px, 2.8vw, 12px)',
                              color: '#6b7280',
                              fontWeight: '500',
                              flexShrink: 0
                            }}>
                              {new Date(resume.upload_timestamp).toLocaleDateString()}
                            </span>
                            <span style={{
                              color: '#d1d5db',
                              fontSize: '12px',
                              flexShrink: 0
                            }}>•</span>
                            <IonChip outline={false} style={{
                              '--background': statusColor === '#28a745' ? '#dcfce7' :
                                statusColor === '#fd7e14' ? '#fef3c7' : '#fecaca',
                              '--color': statusColor,
                              fontSize: 'clamp(10px, 2.5vw, 11px)',
                              fontWeight: '600',
                              height: 'clamp(18px, 4.5vw, 20px)',
                              paddingLeft: 'clamp(6px, 1.5vw, 8px)',
                              paddingRight: 'clamp(6px, 1.5vw, 8px)',
                              margin: 0,
                              flexShrink: 0
                            }}>
                              {statusText}
                            </IonChip>
                          </div>

                          {/* Right side: Action buttons */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            flexShrink: 0
                          }}>
                            {resume.analysis_status === 'completed' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(resume.id);
                                }}
                                style={{
                                  '--color': '#6b7280',
                                  '--background-hover': 'rgba(0,0,0,0.04)',
                                  height: '36px',
                                  width: '36px',
                                  '--border-radius': '8px',
                                  minWidth: '36px',
                                  '--padding-start': '0',
                                  '--padding-end': '0'
                                }}
                              >
                                <IonIcon slot="icon-only" icon={shareOutline} style={{
                                  fontSize: '18px'
                                }} />
                              </IonButton>
                            )}

                            {resume.analysis_status === 'completed' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDefault) handleSetDefault(resume.id);
                                }}
                                style={{
                                  '--color': isDefault ? '#f59e0b' : '#6b7280',
                                  '--background-hover': 'rgba(0,0,0,0.04)',
                                  height: '36px',
                                  width: '36px',
                                  '--border-radius': '8px',
                                  minWidth: '36px',
                                  '--padding-start': '0',
                                  '--padding-end': '0'
                                }}
                              >
                                <IonIcon slot="icon-only" icon={isDefault ? star : starOutline} style={{
                                  fontSize: '18px'
                                }} />
                              </IonButton>
                            )}

                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const buttons = generateActionSheetButtons(resume, displayName);
                                setActionSheetButtons(buttons);
                                setActionSheetOpen(resume.id);
                              }}
                              style={{
                                '--color': '#6b7280',
                                '--background-hover': 'rgba(0,0,0,0.04)',
                                height: '36px',
                                width: '36px',
                                '--border-radius': '8px',
                                minWidth: '36px',
                                '--padding-start': '0',
                                '--padding-end': '0'
                              }}
                            >
                              <IonIcon slot="icon-only" icon={ellipsisVertical} style={{
                                fontSize: '18px'
                              }} />
                            </IonButton>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Desktop: Original layout
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                        paddingRight: mobileTokens.spacing.md
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: mobileTokens.spacing.sm
                        }}>
                          {isRenaming ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <IonInput
                                value={newName}
                                onIonInput={(e) => setNewName(e.detail.value!)}
                                style={{
                                  '--color': '#2c3e50',
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  border: '2px solid #667eea',
                                  borderRadius: '4px',
                                  flex: 1
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveRename();
                                  if (e.key === 'Escape') cancelRename();
                                }}
                              />
                              <IonButton
                                onClick={saveRename}
                                fill="solid"
                                color="success"
                                size="small"
                                style={{
                                  fontSize: '12px',
                                  '--padding-start': '8px',
                                  '--padding-end': '8px'
                                }}
                              >
                                Save
                              </IonButton>
                              <IonButton
                                onClick={cancelRename}
                                fill="solid"
                                color="medium"
                                size="small"
                                style={{
                                  fontSize: '12px',
                                  '--padding-start': '8px',
                                  '--padding-end': '8px'
                                }}
                              >
                                Cancel
                              </IonButton>
                            </div>
                          ) : (
                            <IonText>
                              <h3 style={{
                                margin: 0,
                                fontSize: isMobile
                                  ? `${mobileTokens.typography.h3.mobile}px`
                                  : `${mobileTokens.typography.h3.desktop}px`,
                                fontWeight: '600',
                                color: '#1a1a1a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: mobileTokens.spacing.sm,
                                lineHeight: 1.3,
                                letterSpacing: '-0.01em'
                              }}>
                                <span style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: isMobile ? (isDefault ? '140px' : '180px') : 'none',
                                  marginRight: isDefault ? mobileTokens.spacing.sm : 0
                                }}>
                                  {displayName}
                                </span>
                                {isDefault && (
                                  <IonChip style={{
                                    '--background': '#fff3e0',
                                    '--color': '#f57c00',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    height: '18px',
                                    paddingLeft: '6px',
                                    paddingRight: '6px',
                                    flexShrink: 0
                                  }}>
                                    MAIN
                                  </IonChip>
                                )}
                              </h3>
                            </IonText>
                          )}
                          {!isRenaming && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              backgroundColor: statusColor + '15',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: statusColor
                            }}>
                              <span>{statusIcon}</span>
                              {statusText}
                            </div>
                          )}
                        </div>
                        <IonText style={{
                          fontSize: isMobile
                            ? `${mobileTokens.typography.caption.mobile}px`
                            : `${mobileTokens.typography.caption.desktop}px`,
                          color: '#6b7280',
                          fontWeight: '500',
                          lineHeight: 1.4
                        }}>
                          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>Uploaded {new Date(resume.upload_timestamp).toLocaleDateString()}</span>
                            {isDefault && (
                              <>
                                <span style={{ color: '#d1d5db' }}>•</span>
                                <span style={{ color: '#f59e0b', fontWeight: '600' }}>Main Resume</span>
                              </>
                            )}
                          </p>
                        </IonText>
                      </div>
                    )}

                    {/* Action Buttons - Desktop Only */}
                    {!isMobile && (
                      <div style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: 'fit-content',
                        marginLeft: '12px'
                      }}>
                        {!isRenaming && (
                          <>
                            {resume.analysis_status === 'completed' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(resume.id);
                                }}
                                style={{
                                  '--color': '#6b7280',
                                  '--color-hover': '#374151',
                                  height: '36px',
                                  '--border-radius': '4px',
                                  '--background-hover': 'rgba(0,0,0,0.04)'
                                }}
                                title="Share Resume"
                              >
                                <IonIcon slot="icon-only" icon={shareOutline} style={{
                                  fontSize: '16px'
                                }} />
                              </IonButton>
                            )}

                            {resume.analysis_status === 'completed' && (
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDefault) handleSetDefault(resume.id);
                                }}
                                style={{
                                  '--color': isDefault ? '#f59e0b' : '#6b7280',
                                  '--color-hover': isDefault ? '#d97706' : '#374151',
                                  height: '36px',
                                  '--border-radius': '4px',
                                  '--background-hover': 'rgba(0,0,0,0.04)'
                                }}
                                title={isDefault ? "Main Resume" : "Set as Main Resume"}
                              >
                                <IonIcon slot="icon-only" icon={isDefault ? star : starOutline} style={{
                                  fontSize: '16px'
                                }} />
                              </IonButton>
                            )}

                            <IonButton
                              fill="clear"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const buttons = generateActionSheetButtons(resume, displayName);
                                setActionSheetButtons(buttons);
                                setActionSheetOpen(resume.id);
                              }}
                              style={{
                                '--color': '#6b7280',
                                '--color-hover': '#374151',
                                height: '36px',
                                '--border-radius': '4px',
                                '--background-hover': 'rgba(0,0,0,0.04)'
                              }}
                            >
                              <IonIcon slot="icon-only" icon={ellipsisVertical} style={{
                                fontSize: '16px'
                              }} />
                            </IonButton>
                          </>
                        )}
                      </div>
                    )}
                  </IonItem>
                );
              })}
            </IonList>

            {/* Show More/Less Button */}
            {allResumes.length > 2 && (
              <div
                ref={showMoreButtonRef}
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid rgba(99, 102, 241, 0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <IonButton
                  fill="clear"
                  onClick={handleToggleResumes}
                  style={{
                    '--color': '#667eea',
                    '--color-hover': '#5568d3',
                    fontSize: '14px',
                    fontWeight: '600',
                    textTransform: 'none',
                    letterSpacing: '0.3px'
                  }}
                >
                  {showAllResumes ? (
                    <>
                      <IonIcon icon={chevronUpOutline} slot="start" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <IonIcon icon={chevronDownOutline} slot="start" />
                      Show {allResumes.length - 2} More Resume{allResumes.length - 2 > 1 ? 's' : ''}
                    </>
                  )}
                </IonButton>
              </div>
            )}
          </IonCard>
        ) : (
          <IonCard style={{
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            {/* Header for empty state */}
            <IonCardHeader style={{
              borderBottom: '1px solid #f0f0f0'
            }}>
              <IonGrid className="ion-no-padding" style={{ width: '100%' }}>
                <IonRow className="ion-justify-content-between ion-align-items-center" style={{ width: '100%' }}>
                  <IonCol size="4">
                    <h2 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2c3e50',
                      letterSpacing: '-0.5px'
                    }}>
                      Your Resumes (0)
                    </h2>
                  </IonCol>
                  <IonCol size="4"></IonCol>
                  <IonCol size="4" className="ion-text-right">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={e => e.target.files && handleResumeUpload(e.target.files[0])}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                    />
                    <IonButton
                      fill="solid"
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      style={{
                        '--background': '#1e3a8a',
                        '--color': 'white',
                        height: '32px',
                        minWidth: '120px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textTransform: 'none'
                      }}
                    >
                      {isUploading ? (
                        <>
                          <IonSpinner name="crescent" slot="start" style={{ width: '16px', height: '16px' }} />
                          Uploading...
                        </>
                      ) : (
                        <><IonIcon icon={cloudUploadOutline} slot="start" /> Upload Resume</>
                      )}
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardHeader>

            {/* Empty state content */}
            <IonCardContent className="ion-text-center">
              <IonIcon icon={documentAttachOutline} style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }} />
              <h3 style={{ color: '#2c3e50', margin: '0 0 8px', fontSize: '20px', fontWeight: '600' }}>No Resumes Yet</h3>
              <p style={{ color: '#6c757d', margin: 0, fontSize: '16px' }}>
                Upload your first resume to get started with AI-powered analysis
              </p>
            </IonCardContent>
          </IonCard>
        )}
      </div>
    );

    // Default resume analysis section (concise view)
    const defaultAnalysisSection = () => {
      if (!latestAnalysis) return null;

      const contactInfo = getContactInfo(latestAnalysis);
      const technicalSkills = getTechnicalSkills(latestAnalysis);
      const experience = getFormattedExperience(latestAnalysis);

      return (
        <div style={{ marginTop: '32px' }}>

          {/* Professional Identity */}
          <IonCard style={{
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            marginBottom: '24px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              padding: isMobile ? '16px 20px' : '20px 24px',
              borderBottom: '1px solid rgba(99, 102, 241, 0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '600',
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                lineHeight: '1.3'
              }}>
                <IonIcon icon={personCircleOutline} style={{ fontSize: isMobile ? '20px' : '22px', color: '#667eea' }} />
                Professional Identity
              </h3>
            </div>
            <div style={{ padding: isMobile ? '16px 20px' : '24px' }}>
              {/* Name and contact info */}
              <div style={{ textAlign: 'left', marginBottom: isMobile ? '16px' : '20px' }}>
                <h1 style={{
                  fontSize: isMobile ? '20px' : '24px',
                  fontWeight: '700',
                  color: '#2c3e50',
                  margin: '0 0 6px',
                  lineHeight: '1.2'
                }}>
                  {contactInfo.name}
                </h1>
                {contactInfo.location && (
                  <p style={{
                    color: '#495057',
                    margin: '3px 0 10px',
                    fontSize: isMobile ? '14px' : '16px',
                    lineHeight: '1.3'
                  }}>
                    {contactInfo.location}
                  </p>
                )}
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#6c757d',
                  display: 'flex',
                  gap: isMobile ? '12px' : '16px',
                  flexWrap: 'wrap',
                  lineHeight: '1.3'
                }}>
                  {contactInfo.phone && <span>📞 {contactInfo.phone}</span>}
                  {contactInfo.email && <span>✉️ {contactInfo.email}</span>}
                </div>
              </div>

              {/* Professional Summary */}
              <div style={{ textAlign: 'left' }}>
                <p style={{
                  margin: 0,
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: isMobile ? '1.5' : '1.6',
                  color: '#495057',
                  fontStyle: 'italic'
                }}>
                  {latestAnalysis.summary}
                </p>
              </div>
            </div>
          </IonCard>

          {/* Skills */}
          <IonCard style={{
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            marginBottom: '24px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              padding: isMobile ? '16px 20px' : '20px 24px',
              borderBottom: '1px solid rgba(99, 102, 241, 0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '600',
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                lineHeight: '1.3'
              }}>
                <IonIcon icon={codeSlashOutline} style={{ fontSize: isMobile ? '20px' : '22px', color: '#667eea' }} />
                Technical Skills
              </h3>
            </div>
            <div style={{ padding: isMobile ? '16px 20px' : '20px 24px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {technicalSkills.slice(0, 12).map((skill, index) => (
                  <IonChip
                    key={skill.name}
                    style={{
                      '--background': `linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)`,
                      '--color': '#5b21b6',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600',
                      height: isMobile ? '28px' : '32px',
                      border: '1px solid rgba(99, 102, 241, 0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {skill.name}
                  </IonChip>
                ))}
                {technicalSkills.length > 12 && (
                  <span style={{
                    color: '#6c757d',
                    fontSize: isMobile ? '12px' : '14px',
                    alignSelf: 'center',
                    lineHeight: '1.3'
                  }}>
                    +{technicalSkills.length - 12} more
                  </span>
                )}
              </div>
            </div>
          </IonCard>
        </div>
      );
    };

    // Professional mobile-first layout with enhanced responsive design
    const containerStyles = {
      maxWidth: isMobile ? 'none' : isTablet ? '90%' : '70%',
      width: '100%',
      margin: '0 auto',
      padding: isMobile ? '0' : `0 ${mobileTokens.spacing.lg}px`,
      minHeight: '100vh',
      background: 'transparent'
    };

    // Credits sidebar component
    const creditsSidebar = () => {
      return (
        <IonList className="ion-no-padding" style={{
          position: 'sticky',
          top: '24px',
          background: 'transparent'
        }}>
          {/* Saved Jobs Card - Highlighted */}
          <IonCard style={{
            '--background': 'rgba(30, 58, 138, 0.85)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
            margin: 0,
            marginBottom: '24px',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onClick={() => history.push('/app/jobs/saved')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)';
          }}
          >
            <div style={{ padding: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <IonIcon icon={bookmark} style={{ fontSize: '20px', color: 'white' }} />
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Saved Jobs
                  </div>
                </div>
                {savedJobs.length > 0 && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: 'white'
                  }}>
                    {savedJobs.length}
                  </div>
                )}
              </div>
              {savedJobsLoading ? (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <IonSpinner style={{ '--color': 'white' }} />
                </div>
              ) : savedJobs.length === 0 ? (
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.5'
                }}>
                  Save jobs you're interested in to keep track of opportunities
                </div>
              ) : (
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.5'
                }}>
                  {savedJobs.slice(0, 2).map((sj: any, idx: number) => (
                    <div key={sj.id} style={{
                      marginBottom: idx < 1 ? '6px' : '0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      • {sj.scraped_job?.title || 'Job'}
                    </div>
                  ))}
                  {savedJobs.length > 2 && (
                    <div style={{ marginTop: '6px', fontWeight: '600' }}>
                      +{savedJobs.length - 2} more
                    </div>
                  )}
                </div>
              )}
            </div>
          </IonCard>

          {/* Available Credits Card */}
          <IonCard style={{
            marginBottom: '16px',
            '--background': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 4px rgba(0,0,0,0.04)',
            margin: 0,
            overflow: 'hidden'
          }}>
            <div style={{ padding: '24px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.8)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px'
              }}>
                Available Credits
              </div>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#ffffff',
                lineHeight: '1',
                marginBottom: '8px'
              }}>
                {creditBalance?.balance?.toLocaleString() || 0}
              </div>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                Ready to use
              </div>
            </div>
          </IonCard>

          {/* Usage This Month Card */}
          <IonCard style={{
            marginBottom: '16px',
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '16px'
              }}>
                Usage This Month
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#2c3e50',
                marginBottom: '4px'
              }}>
                {usageSummary?.total_credits_used?.toLocaleString() || 0}
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden',
                marginTop: '12px'
              }}>
                <div style={{
                  width: `${Math.min(100, ((usageSummary?.total_credits_used || 0) / ((creditBalance?.balance || 0) + (usageSummary?.total_credits_used || 0))) * 100)}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{
                fontSize: '12px',
                color: '#9ca3af',
                marginTop: '8px'
              }}>
                {usageSummary?.total_calls || 0} AI calls • {(usageSummary?.avg_credits_per_call || 0).toFixed(1)} avg/call
              </div>
            </div>
          </IonCard>

          {/* Credit Activity Card */}
          <IonCard style={{
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '16px'
              }}>
                Recent Activity
              </div>

              {/* Activity Items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {creditTransactions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: '13px' }}>
                    No recent activity
                  </div>
                ) : (
                  creditTransactions.slice(0, 3).map((tx, index: number) => {
                    const isPositive = tx.amount > 0;
                    const dateStr = tx.created_on || tx.created_at || '';
                    const date = dateStr ? new Date(dateStr) : new Date();
                    const timeAgo = getTimeAgo(date);

                    return (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#2c3e50' }}>
                            {tx.description || tx.transaction_type}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{timeAgo}</div>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: isPositive ? '#059669' : '#dc2626'
                        }}>
                          {isPositive ? '+' : ''}{tx.amount}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </IonCard>

          {/* Get More Credits Card */}
          <IonCard style={{
            '--background': '#ffffff',
            borderRadius: '12px',
            border: '1px solid rgba(99, 102, 241, 0.1)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
            margin: 0,
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '8px'
              }}>
                Need More Credits?
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '16px',
                lineHeight: '1.5'
              }}>
                Unlock more features and capabilities with additional credits
              </div>
              <IonButton
                expand="block"
                onClick={() => history.push('/app/settings/billing')}
                style={{
                  '--background': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '--border-radius': '8px',
                  height: '44px',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'none'
                }}
              >
                Purchase Credits
              </IonButton>
            </div>
          </IonCard>
        </IonList>
      );
    };

    return (
      <div style={{
        display: 'flex',
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '0' : isTablet ? '16px 24px' : '24px 48px',
        minHeight: '100vh',
        gap: isDesktop ? '24px' : '0'
      }}>
        {/* Sidebar - Only on desktop (1024px+) */}
        {isDesktop && (
          <div style={{
            width: '280px',
            flexShrink: 0
          }}>
            {creditsSidebar()}
          </div>
        )}

        {/* Main Content Area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <IonGrid style={containerStyles} className="ion-no-padding">
            <IonRow>
              <IonCol size="12">
                {resumeManagementSection}

                {/* Action Buttons (Mobile, Tablet, and Desktop) */}
                <div style={{
                  margin: isMobile ? '12px' : '16px 0',
                  width: isMobile ? 'calc(100% - 24px)' : '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {/* Saved Jobs Button */}
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={() => history.push('/app/jobs/saved')}
                    style={{
                      '--background': 'rgba(30, 58, 138, 0.85)',
                      '--background-activated': 'rgba(30, 58, 138, 0.95)',
                      '--border-radius': '10px',
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textTransform: 'none'
                    }}
                  >
                    <IonIcon icon={bookmark} slot="start" />
                    Saved Jobs {savedJobs.length > 0 && `(${savedJobs.length})`}
                  </IonButton>

                  {/* Preferences Button */}
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={() => history.push('/app/profile/preferences')}
                    style={{
                      '--border-color': 'rgba(99, 102, 241, 0.5)',
                      '--color': 'rgba(99, 102, 241, 1)',
                      '--background-activated': 'rgba(99, 102, 241, 0.1)',
                      '--border-radius': '10px',
                      '--border-width': '2px',
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textTransform: 'none'
                    }}
                  >
                    <IonIcon icon={listOutline} slot="start" />
                    Job Preferences
                  </IonButton>
                </div>

                {defaultAnalysisSection()}
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </div>
    );
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <TitleBar title="Profile">
          <IonButton fill="clear" onClick={handleLogout}>
            <IonIcon icon={logOutOutline} style={{ fontSize: '26px' }} />
          </IonButton>
        </TitleBar>
      </IonHeader>
      <IonContent className={isMobile ? '' : 'ion-padding'} style={{
        '--background': '#f8fafc',
        background: `
          radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.03) 0%, transparent 50%),
          repeating-linear-gradient(
            45deg,
            transparent,
            transparent 40px,
            rgba(99, 102, 241, 0.015) 40px,
            rgba(99, 102, 241, 0.015) 80px
          ),
          linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)
        `
      }}>
        {/* Show creative loader with jokes while mounting or setting up */}
        {(isMounting || isSettingUpExperience || (loading && !dataLoaded)) ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh'
          }}>
            <CreativeLoader
              size="large"
              message="Preparing Your Dashboard..."
              showJoke={true}
            />
          </div>
        ) : (
          <>
            {renderContent()}
            <ShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              resumeId={selectedResumeId}
            />

            {/* Modern Rename Modal */}
            <IonModal
              isOpen={!!renamingId}
              onDidDismiss={cancelRename}
              backdropDismiss={true}
              style={{
                '--width': '90%',
                '--max-width': '500px',
                '--height': 'auto',
                '--border-radius': '20px',
                '--box-shadow': '0 20px 60px rgba(102, 126, 234, 0.3)'
              }}
            >
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '40px 32px',
                borderRadius: '20px',
                animation: 'modalSlideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                {/* Header */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: '32px',
                  animation: 'fadeInDown 0.5s ease-out'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both'
                  }}>
                    <IonIcon
                      icon={documentAttachOutline}
                      style={{
                        fontSize: '32px',
                        color: '#ffffff'
                      }}
                    />
                  </div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#ffffff',
                    letterSpacing: '0.5px'
                  }}>
                    Rename Resume
                  </h2>
                  <p style={{
                    margin: '8px 0 0',
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: '400'
                  }}>
                    Give your resume a memorable name
                  </p>
                </div>

                {/* Input Field */}
                <div style={{
                  marginBottom: '24px',
                  animation: 'fadeInUp 0.5s ease-out 0.2s both'
                }}>
                  <IonInput
                    value={newName}
                    onIonInput={(e) => setNewName(e.detail.value!)}
                    placeholder="Enter resume name..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename();
                      if (e.key === 'Escape') cancelRename();
                    }}
                    style={{
                      '--background': 'rgba(255, 255, 255, 0.95)',
                      '--color': '#2c3e50',
                      '--placeholder-color': '#9ca3af',
                      '--padding-start': '20px',
                      '--padding-end': '20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      borderRadius: '12px',
                      minHeight: '56px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                  />
                </div>

                {/* Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  animation: 'fadeInUp 0.5s ease-out 0.3s both'
                }}>
                  <IonButton
                    onClick={cancelRename}
                    expand="block"
                    style={{
                      '--background': 'rgba(255, 255, 255, 0.2)',
                      '--background-hover': 'rgba(255, 255, 255, 0.3)',
                      '--background-activated': 'rgba(255, 255, 255, 0.15)',
                      '--color': '#ffffff',
                      '--border-radius': '12px',
                      '--box-shadow': 'none',
                      fontSize: '15px',
                      fontWeight: '600',
                      height: '50px',
                      flex: 1,
                      textTransform: 'none',
                      letterSpacing: '0.3px'
                    }}
                  >
                    Cancel
                  </IonButton>
                  <IonButton
                    onClick={saveRename}
                    expand="block"
                    disabled={!newName.trim()}
                    style={{
                      '--background': '#ffffff',
                      '--background-hover': '#f8f9fa',
                      '--background-activated': '#e9ecef',
                      '--color': '#667eea',
                      '--border-radius': '12px',
                      '--box-shadow': '0 4px 12px rgba(0, 0, 0, 0.15)',
                      fontSize: '15px',
                      fontWeight: '700',
                      height: '50px',
                      flex: 1,
                      textTransform: 'none',
                      letterSpacing: '0.3px',
                      opacity: newName.trim() ? 1 : 0.5
                    }}
                  >
                    Save Name
                  </IonButton>
                </div>
              </div>

              <style>{`
                @keyframes modalSlideUp {
                  from {
                    opacity: 0;
                    transform: translateY(30px) scale(0.95);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0) scale(1);
                  }
                }

                @keyframes fadeInDown {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                @keyframes scaleIn {
                  from {
                    opacity: 0;
                    transform: scale(0);
                  }
                  to {
                    opacity: 1;
                    transform: scale(1);
                  }
                }
              `}</style>
            </IonModal>
          </>
        )}

        <IonActionSheet
          isOpen={!!actionSheetOpen}
          onDidDismiss={() => setActionSheetOpen(null)}
          buttons={actionSheetButtons}
          header={isMobile ? "Choose an action" : "Resume Actions"}
          subHeader={isMobile ? "What would you like to do with this resume?" : undefined}
          mode={isMobile ? "ios" : "md"}
          cssClass={isMobile ? "mobile-action-sheet" : "desktop-action-sheet"}
        />


        {/* Preference Onboarding Modal */}
        {userPreferences && (
          <PreferenceOnboardingModal
            isOpen={showPreferenceOnboarding}
            onDismiss={() => {
              setShowPreferenceOnboarding(false);
            }}
            preferences={userPreferences}
            onComplete={(updatedPreferences) => {
              setUserPreferences(updatedPreferences);
              setShowPreferenceOnboarding(false);
              presentToast({
                message: '✅ Preferences saved successfully!',
                duration: 2000,
                color: 'success',
              });
            }}
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default Profile;